<template>
  <div class="mall-overview">
    <ContentWrap class="mb-16px">
      <div class="page-header">
        <div>
          <h2 class="page-title">商城经营概况</h2>
          <p class="page-subtitle">今日经营表现、常用运营入口与交易趋势</p>
        </div>
        <el-button :icon="Refresh" :loading="loading" @click="loadSummary">刷新</el-button>
      </div>
    </ContentWrap>

    <ContentWrap title="装修商城" class="mb-16px">
      <el-row :gutter="16">
        <el-col v-for="item in decorateCards" :key="item.title" :span="24" class="mb-16px">
          <button
            type="button"
            class="decorate-card"
            :class="item.className"
            @click="openDecorate(item.path)"
          >
            <div>
              <div class="decorate-card__title">{{ item.title }}</div>
              <div class="decorate-card__desc">{{ item.desc }}</div>
              <span class="decorate-card__action">去装修</span>
            </div>
            <Icon :icon="item.icon" :size="46" class="decorate-card__icon" />
          </button>
        </el-col>
      </el-row>
    </ContentWrap>

    <el-skeleton :loading="loading" animated>
      <template #template>
        <el-row :gutter="16" class="mb-16px">
          <el-col v-for="index in 4" :key="index" :xs="24" :sm="12" :lg="6" class="mb-16px">
            <el-skeleton-item variant="rect" class="summary-skeleton" />
          </el-col>
        </el-row>
      </template>
      <template #default>
        <el-row :gutter="16" class="mb-16px">
          <el-col :xs="24" :sm="12" :lg="6" class="mb-16px">
            <ComparisonCard
              tag="今日"
              title="销售额"
              prefix="￥"
              :decimals="2"
              :value="fenToYuanNumber(orderComparison?.value?.orderPayPrice || 0)"
              :reference="fenToYuanNumber(orderComparison?.reference?.orderPayPrice || 0)"
            />
          </el-col>
          <el-col :xs="24" :sm="12" :lg="6" class="mb-16px">
            <ComparisonCard
              tag="今日"
              title="用户访问量"
              :value="Number(userComparison?.value?.visitUserCount || 0)"
              :reference="Number(userComparison?.reference?.visitUserCount || 0)"
            />
          </el-col>
          <el-col :xs="24" :sm="12" :lg="6" class="mb-16px">
            <ComparisonCard
              tag="今日"
              title="订单量"
              :value="orderComparison?.value?.orderPayCount || 0"
              :reference="orderComparison?.reference?.orderPayCount || 0"
            />
          </el-col>
          <el-col :xs="24" :sm="12" :lg="6" class="mb-16px">
            <ComparisonCard
              tag="今日"
              title="新增用户"
              :value="userComparison?.value?.registerUserCount || 0"
              :reference="userComparison?.reference?.registerUserCount || 0"
            />
          </el-col>
        </el-row>
      </template>
    </el-skeleton>

    <!-- 从总裁驾驶舱迁入：概况承载日常运营跳转，驾驶舱专注数据大屏 -->
    <ShortcutCard class="mb-16px" />

    <TradeTrendCard class="mb-16px" />
  </div>
</template>

<script setup lang="ts">
import { Refresh } from '@element-plus/icons-vue'
import * as TradeStatisticsApi from '@/api/mall/statistics/trade'
import * as MemberStatisticsApi from '@/api/mall/statistics/member'
import type { DataComparisonRespVO } from '@/api/mall/statistics/common'
import type { TradeOrderSummaryRespVO } from '@/api/mall/statistics/trade'
import type { MemberCountRespVO } from '@/api/mall/statistics/member'
import { fenToYuanNumber } from '@/utils'
import ComparisonCard from '@/views/mall/home/components/ComparisonCard.vue'
import ShortcutCard from '@/views/mall/home/components/ShortcutCard.vue'
import TradeTrendCard from '@/views/mall/home/components/TradeTrendCard.vue'

defineOptions({ name: 'MallOverview' })

const router = useRouter()
const loading = ref(true)
const orderComparison = ref<DataComparisonRespVO<TradeOrderSummaryRespVO>>()
const userComparison = ref<DataComparisonRespVO<MemberCountRespVO>>()

const decorateCards = [
  {
    title: '装修模板',
    desc: '多业态商城首页：拖拽排版后须发布并应用才生效',
    icon: 'ep:brush',
    className: 'is-green',
    path: '/diy/template/list'
  }
]

async function loadSummary() {
  loading.value = true
  try {
    const [orderData, userData] = await Promise.all([
      TradeStatisticsApi.getOrderComparison(),
      MemberStatisticsApi.getUserCountComparison()
    ])
    orderComparison.value = orderData
    userComparison.value = userData
  } finally {
    loading.value = false
  }
}

/** 进入统一的移动端首页装修模板（与酒店等同业态入口一致） */
function openDecorate(_path: string) {
  router.push({ path: '/diy/template/list', query: { industry: 'mall', scene: 'home' } })
}

onMounted(loadSummary)
</script>

<style scoped lang="scss">
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}

.page-subtitle {
  margin: 5px 0 0;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.summary-skeleton {
  width: 100%;
  height: 142px;
  border-radius: 6px;
}

.decorate-card {
  display: flex;
  width: 100%;
  min-height: 124px;
  padding: 20px 24px;
  color: #fff;
  text-align: left;
  cursor: pointer;
  border: 0;
  border-radius: 6px;
  transition:
    box-shadow 0.15s ease,
    transform 0.15s ease;
  align-items: center;
  justify-content: space-between;

  &.is-green {
    background: linear-gradient(110deg, #16a34a, #4ade80);
  }

  &.is-orange {
    background: linear-gradient(110deg, #f97316, #fb923c);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 7px 18px rgb(0 0 0 / 12%);
  }
}

.decorate-card__title {
  font-size: 18px;
  font-weight: 600;
}

.decorate-card__desc {
  margin-top: 5px;
  font-size: 13px;
  color: rgb(255 255 255 / 84%);
}

.decorate-card__action {
  display: inline-block;
  padding: 5px 14px;
  margin-top: 15px;
  font-size: 12px;
  background: rgb(255 255 255 / 14%);
  border: 1px solid rgb(255 255 255 / 58%);
  border-radius: 16px;
}

.decorate-card__icon {
  flex-shrink: 0;
  opacity: 0.9;
}

@media (width <= 600px) {
  .page-header {
    align-items: flex-start;
    gap: 12px;
  }

  .decorate-card {
    padding: 18px;
  }
}
</style>
